import { Injectable } from '@angular/core';
import { of, Observable, Subject, BehaviorSubject } from 'rxjs';
import { HttpUtilService } from '@app/services/http-util.service';
import { environment } from '@env/environment';
import {
  NotificationsService,
  NotificationItem,
} from '@services/notifications.service';
import { Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { LocalizeDatePipe } from '@app/shared/pipe.module';
import { CopyDataService } from './copy-data.service';
import { TranslationService } from '@services/translation.service';

/**
 * A course version.
 *
 */

export class CourseVersion {
  /** The id? */
  id?: number;
  /** The name */
  name = '';
  /** created at date */
  created_at?: string;
  /** The description */
  description = '';
  /** User that created the version */
  user?: any;
}

@Injectable({
  providedIn: 'root',
})
export class VersionControlService {
  /** Currently displayed version*/
  currentVersion;
  /** Subject for any version changes*/
  versionUpdater: Subject<any> = new Subject<any>();
  /** Notification item containing the version message*/
  versionMessage: NotificationItem;
  /** state of the version*/
  private _state = false;
  /** . */
  versiondisplay: BehaviorSubject<any> = new BehaviorSubject<any>(this._state);

  constructor(
    public http: HttpUtilService,
    private notifications: NotificationsService,
    private router: Router,
    private datePipe: LocalizeDatePipe,
    private copyDataService: CopyDataService,
    private translationService: TranslationService
  ) {}

  /**
   * Gets the versions
   *
   * @param  id      The identifier.
   * @param   type    The type.
   */

  getVersions(id: number, type: string): Observable<any> {
    // this.updateItem(id)
    return this.http.request(
      'GET',
      environment.apiRoot + '/courses/' + id + '?get_versions=true',
      null,
      null,
      true
    );
  }

  /**
   * Creates a version
   *
   * @param      id          The identifier.
   * @param      type        The type.
   * @param     version The version.
   *
   */

  createVersion(
    id: number,
    type: string,
    version: CourseVersion
  ): Observable<any> {
    return this.http.request(
      'PUT',
      environment.apiRoot + '/courses/' + id + '?set_version=true',
      { data: { name: version.name, description: version.description } },
      null,
      true
    );
  }

  /**
   * Sets a version
   * @param     id  version to set.
   *
   */

  setVersion(version) {
    if (this.versionMessage) {
      this.versionMessage?.onClose?.complete();
      this.notifications.dismissBar(this.versionMessage);
    }
    if (version) {
      if (version.id !== this.currentVersion) {
        this.currentVersion = version.id;

        this.versionMessage = new NotificationItem({
          message:
            '<p><strong>You Are Viewing A Version of this Course:</strong> "' +
            version.name +
            '" ' +
            this.datePipe.transform(version.created_at, 'medium') +
            '</p>',
          status: 'warning',
          close: 'Exit Version',
        });
        this.notifications
          .createBarNotification(this.versionMessage)
          .pipe(take(1))
          .subscribe(v => {
            this.setVersion(null);
          });
      }
    }
    this.currentVersion = version?.id;
    this.versionUpdater.next(version?.id);
  }

  /**
   * Subscribe to version change
   */

  watchVersion(): Observable<any> {
    return this.versionUpdater.asObservable();
  }

  /**
   * Create a new data item from specified version
   * @param     id      The original data ID.
   * @param     type    The type.
   * @param     version The version to get data from.
   *
   */

  createFromVersion(id, type, version) {
    this.copyDataService
      .initCopySocket(id)
      .pipe(take(1))
      .subscribe(e => {
        const postBody = {
          data: {},
          type: type,
        };
        this.http
          .request(
            'POST',
            `${environment.apiRoot}/${type}?duplicate=${id}&version=${version}&connection=${e}`,
            postBody,
            null,
            true
          )
          .subscribe(newData => {
            if (newData.errorMessage) {
              const text = this.translationService.getTranslatedContent(
                'there_was_an_issue_creating_a_new',
                'There was an issue creating a new'
              );
              const endtext = this.translationService.getTranslatedContent(
                'at_this_time._please_try_again_later._if_the_problem_persists_please_contact_an_administrator.',
                'There was an issue creating a new'
              );

              this.notifications.popNotification(
                `${text} ` +
                  this.translationService.getTranslatedContent(type) +
                  ' ' +
                  endtext,
                'X',
                5
              );
            }
          });
      });
  }
}
